import { useEffect, useContext, useState, useCallback } from "react";
import moment from "moment";
import { connect } from "react-redux";
import PhoneInput from "react-phone-number-input";
import { debounce } from "lodash";
import { stateType } from "../../store";
import { BlackListWrapper } from "../../pages/CheckOut/CheckOut";
import CheckOutButtons from "../../pages/CheckOut/CheckOutButtons";
import { setOrderDetails } from "../../state_management/actions";
import {
  isComplete,
  orderType,
  proceed_val_type
} from "../../state_management/reducers/order";
import Input from "../../UI/Input/Input";
import AuthService from "../../utilities/accountAuth";
import { getCookie } from "../../utilities/cookieManager";
import CheckOutList from "../CheckOutList/CheckOutList";
import "react-phone-number-input/style.css";
import "./CheckOutAddress.css";
import "../CheckOutLogin/CheckOutLogin.css";
import {
  coupon_error_message,
  getCurrency,
  isEmail,
  isMobile
} from "../../utilities/misc";
import {
  setCheckOutError,
  setEmailBlacklisted,
  setPageCount
} from "../../state_management/actions/common";
import { useAsync } from "../../utilities/asyncFunc";
import { checkEmail } from "../../state_management/epics/order";
import {
  ga_params_type,
  add_shipping_info_ga
} from "../../utilities/analytics";
import { cartItem, extrasCartItem } from "../../state_management/reducers/cart";
import { Raw } from "../../App";
import { domain as d } from "../../settings";
import {
  epics_cart_items,
  only_gift,
  split_cart
} from "../../utilities/cartUtilities";
import {
  CouponObjectContext,
  LocaleContext,
  TotalWithCouponContext
} from "../../AppProvider";
import CheckBox from "../../UI/CheckBox/CheckBox";
import {
  addressCookieKey,
  eircodeCookieKey
} from "../CheckOutTime/CheckOutTime";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const fullName = (firstname: string | null, surname: string | null): string => {
  firstname = firstname || ``;
  const hasSurname = typeof surname === "string";
  const fullName = firstname + (hasSurname ? ` ${surname}` : "");
  return fullName;
};

const splitNames = (input: string): string[] => {
  const trimmedInput = input.trim(); // Remove leading and trailing spaces
  const splitArray = trimmedInput.split(/\s+/); // Split by one or more spaces

  const trailingSpaces = " ".repeat(
    (() => {
      const match = input.match(/[^ ]\s*$/);
      if (match) {
        return match[0].length - 1; // Subtract 1 to exclude the non-space character
      }
      return 0;
    })()
  );

  // Add back leading and trailing spaces if present
  // splitArray[0] = leadingSpaces + splitArray[0];
  splitArray[splitArray.length - 1] += trailingSpaces;

  return splitArray;
};

const addressSchema = z.object({
  name: z.string()
    .min(1, "Please enter your first name and your last name")
    .refine((val) => {
      const arr = splitNames(val);
      return arr.length >= 1 && arr[0].trim().length > 0;
    }, "Please enter your first name")
    .refine((val) => {
      const arr = splitNames(val);
      return arr.length >= 2 && arr[1].trim().length > 0;
    }, "Please enter your last name"),
  client_email: z.string().min(1, "Please enter your email").email("Please enter a valid email"),
  client_mobile: z.string().min(1, "Please enter your phone number").refine((val) => isMobile(val), "Please enter a valid phone number"),
  eircode: z.string().optional(),
  info: z.string().optional()
});

type AddressFormValues = z.infer<typeof addressSchema>;

const CheckOutAddress = ({
  orderDetails,
  setOrderDetails,
  checkoutError,
  emailBlacklisted,
  setEmailBlackListed,
  setPageCount,
  pageCount,
  setCheckOutError,
  cart,
  extras
}: {
  orderDetails: orderType;
  setOrderDetails: (details: Partial<orderType>) => void;
  checkoutError: boolean;
  emailBlacklisted: boolean;
  setEmailBlackListed: (data: boolean) => void;
  setPageCount: (data: number) => void;
  pageCount: proceed_val_type;
  setCheckOutError: (data: boolean) => void;
  cart: cartItem[];
  extras: extrasCartItem[];
}) => {
  const { t, locale } = useContext(LocaleContext);
  const [totalWithCoupon] = useContext(TotalWithCouponContext);
  const [noEircodeAvailable, setNoEircodeAvailable] = useState(() => orderDetails.eircode === null);
  const isAuthenticated = !!AuthService.getAuthToken();
  const [showAuthMessage, setShowAuthMessage] = useState(() => {
    return !isAuthenticated && !sessionStorage.getItem("hideAuthMessage");
  });

  const handleCloseAuthMessage = () => {
    setShowAuthMessage(false);
    sessionStorage.setItem("hideAuthMessage", "true");
  };



  const { control, handleSubmit, formState: { errors }, trigger, watch, reset } = useForm<AddressFormValues>({
    resolver: zodResolver(
      addressSchema.refine((data) => noEircodeAvailable || (data.eircode && data.eircode.trim().length > 0), {
        message: "Please enter your Eircode or check the box",
        path: ["eircode"]
      })
    ),
    defaultValues: {
      name: fullName(orderDetails.client_firstname, orderDetails.client_lastname),
      client_email: orderDetails.client_email || "",
      client_mobile: orderDetails.client_mobile || "",
      eircode: orderDetails.eircode || "",
      info: orderDetails.info || ""
    }
  });

  useEffect(() => {
    reset({
      name: fullName(orderDetails.client_firstname, orderDetails.client_lastname),
      client_email: orderDetails.client_email || "",
      client_mobile: orderDetails.client_mobile || "",
      eircode: orderDetails.eircode || "",
      info: orderDetails.info || ""
    });
  }, [orderDetails.client_firstname, orderDetails.client_lastname, orderDetails.client_email, orderDetails.client_mobile, orderDetails.eircode, orderDetails.info, reset]);
  const { cart: cart_items, gift_values: gift } = split_cart(cart);
  const onlyGift = only_gift({ cart, extras });
  const proceed = isComplete(pageCount, orderDetails, false, onlyGift);

  const {
    CouponObject: { couponRes, couponErrorType, errorBanner }
  } = useContext(CouponObjectContext);

  const emailBlackListCheckRes = useAsync({
    asyncFunc: checkEmail,
    funcParams: {},
    immediate: false,
    clearOnError: true
  });

  const parseCart: ga_params_type["items"] = [
    ...cart.map((item) => ({
      id: item.id,
      type: item.type,
      quantity: item.quantity,
      item: item.item,
      extra: false
    })),
    ...extras.map((item) => ({
      id: item.id,
      type: item.type,
      item: item.item,
      extra: true,
      quantity: item.quantity
    }))
  ];

  const nextFunction = async (data: AddressFormValues) => {
    try {
      sessionStorage.setItem("hideAuthMessage", "true");
      const nameArray = splitNames(data.name);
      const client_firstname = nameArray[0] || null;
      const client_lastname = nameArray.length > 1 ? nameArray.slice(1).join(` `) : null;
      
      const partialDetails = {
        client_firstname,
        client_lastname,
        client_email: data.client_email,
        client_mobile: data.client_mobile,
        eircode: !noEircodeAvailable ? (data.eircode || null) : null,
        info: data.info || null
      };
      
      setOrderDetails(partialDetails);

      add_shipping_info_ga({
        coupon: orderDetails.coupon_code,
        currency: getCurrency(),
        items: parseCart,
        value: totalWithCoupon
      });

      if (orderDetails.coupon_code) {
        const res = await couponRes.execute({
          coupon_code: orderDetails.coupon_code,
          details: { ...orderDetails, ...partialDetails },
          show_banner: true,
          ...epics_cart_items({ cart: cart_items, extras, gift_values: gift })
        });

        if (!res) return;
      }

      // make the request
      emailBlackListCheckRes.execute({
        email: partialDetails.client_email || undefined
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleNextClick = () => {
    handleSubmit(nextFunction, () => {
      setCheckOutError(true);
    })();
  };

  useEffect(() => {
    const called = emailBlackListCheckRes.called;
    const success = !!emailBlackListCheckRes.data?.success;

    if (called && !emailBlackListCheckRes.error && success) {
      // if successful, proceed to order page by increasing count.
      setPageCount(pageCount + 1);
    }
  }, [emailBlackListCheckRes.data]);

  useEffect(() => {
    const hasError = !!emailBlackListCheckRes.error;

    if (hasError) {
      // If usuccessful, show error
      setEmailBlackListed(true);
    } else {
      setEmailBlackListed(false);
    }
  }, [emailBlackListCheckRes.error]);

  useEffect(() => {
    return () => {
      setEmailBlackListed(false);
    };
  }, []);



  const loading = emailBlackListCheckRes.loading;
  const error = emailBlackListCheckRes.error;

  const defaultPhoneCountry = locale === "eng" ? "IE" : "LT";
  const domain = d(locale);

  return (
    <div className="checkOutAddress_wrapper">
      <BlackListWrapper
        listObjects={[
          {
            inView: emailBlacklisted,
            text: error ? error : t("Email is blacklisted"),
            watch: !!(!loading && error && emailBlacklisted)
          },
          {
            inView: errorBanner && (!!couponRes.error || !!couponErrorType),
            text: couponErrorType
              ? coupon_error_message(couponErrorType, t)
              : "Couldn't apply coupon",
            watch: !!(
              !couponRes.loading &&
              errorBanner &&
              (!!couponRes.error || !!couponErrorType)
            )
          }
        ]}
      />
      <div className="flex checkout_content_wrapper">
        <div className="checkout_left">
          <div>
            <p className="checkoutAddress_introLabel med_text">
              {t("Please provide your details in the form below to proceed.")}
            </p>
            {showAuthMessage && (
              <div className="no-account-toast" style={{ position: "fixed", top: "24px", right: "24px", bottom: "auto", left: "auto", margin: 0, width: "auto", maxWidth: "400px", zIndex: 9999, animation: "slideDownFade 0.3s ease-out forwards" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="11.5" stroke="#08344B"/>
                  <path d="M7.5 12L10.5 15L16.5 9" stroke="#08344B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="no-account-toast-text">
                  {t("Please check your inbox if you want your details to be filled automatically" as any) || "Please check your inbox if you want your details to be filled automatically"}
                </span>
                <button className="no-account-toast-close" onClick={handleCloseAuthMessage} type="button">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L13 13M1 13L13 1" stroke="#08344B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            <div className="checkout_content">
              <form onSubmit={handleSubmit(nextFunction, () => setCheckOutError(true))}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={t("Name")}
                      error={errors.name?.message}
                      value={field.value}
                      handler={field.onChange}
                    />
                  )}
                />
                <Controller
                  name="client_email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={t("Email")}
                      type="email"
                      error={errors.client_email?.message}
                      value={field.value}
                      handler={field.onChange}
                      readOnly={isAuthenticated}
                    />
                  )}
                />
                <div className="input_wrapper ">
                  <label className="flex med_text">{t("Phone Number")}</label>
                  <Controller
                    name="client_mobile"
                    control={control}
                    render={({ field }) => (
                      <PhoneInput
                        addInternationalOption={false}
                        placeholder=""
                        defaultCountry={defaultPhoneCountry}
                        value={field.value || undefined}
                        onChange={(mobile) => field.onChange(mobile ? (mobile as string) : "")}
                      />
                    )}
                  />
                  {errors.client_mobile?.message && (
                    <span className="errorMsg">{errors.client_mobile.message}</span>
                  )}
                </div>
                <div className="checkout_pseudoAddress_wrapper">
                  <Input
                    label={t("Address")}
                    readOnly
                    value={orderDetails.client_address || getCookie(addressCookieKey)}
                    handler={() => {}}
                  />
                </div>
                <div className="checkout_eircode_wrapper">
                  {!noEircodeAvailable && (
                    <Controller
                      name="eircode"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label={
                            <label className="eircode_label flex">
                              <span className="med_text">Eircode&nbsp; </span>
                              <span className="reg_text">
                                (Find it{" "}
                                <a
                                  href="https://finder.eircode.ie"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  here
                                </a>
                                )
                              </span>
                            </label>
                          }
                          value={field.value || ""}
                          handler={field.onChange}
                          error={errors.eircode?.message}
                        />
                      )}
                    />
                  )}
                  <div className="eircode_check_wrapper">
                    <span
                      onClick={() => {
                        setNoEircodeAvailable((open) => {
                          const newState = !open;
                          if (newState) {
                            control._formValues.eircode = getCookie(eircodeCookieKey) ?? "";
                          } else {
                            control._formValues.eircode = "";
                          }
                          return newState;
                        });
                      }}
                    >
                      <CheckBox checked={noEircodeAvailable} />
                      <span>I don’t have an Eircode.</span>
                    </span>
                  </div>
                </div>
                <Raw>
                  {`
                    <p class="checkoutTerms showSm">
                      ${t(
                        "By proceeding with your booking you agree to our {{Terms of service}} and {{Privacy Policy}}. We may occasionally send you newsletters with special offers. Don’t worry, you can easily unsubscribe at anytime.",
                        {
                          "{{Terms of service}}": `<span class="fontBlue"><a href="https://${domain}/terms-and-conditions/" target="_blank" rel="noreferrer">${t(
                            "Terms of service"
                          )}</a></span>`,
                          "{{Privacy Policy}}": ` <span class="fontBlue"><a href="https://${domain}/privacy-policy/" target="_blank" rel="noreferrer">${t(
                            "Privacy Policy"
                          )}</a></span>`
                        }
                      )}
                    </p>
                  `}
                </Raw>
                <div className="checkout_textArea_wrapper">
                  <span className="med_text">{t("Notes (optional)")}</span>
                  <Controller
                    name="info"
                    control={control}
                    render={({ field }) => (
                      <textarea
                        onChange={field.onChange}
                        value={field.value || ``}
                        className="checkout_textArea"
                      ></textarea>
                    )}
                  />
                </div>
              </form>
            </div>
            <CheckOutButtons
              nextFunction={handleNextClick}
              loading={couponRes.loading || loading}
            />
          </div>
        </div>
        <div className="checkout_right">
          <CheckOutList
            nextFunction={handleNextClick}
            loading={couponRes.loading || loading}
          />
          <Raw>
            {`
              <p class="checkoutTerms showLg">
                ${t(
                  "By proceeding with your booking you agree to our {{Terms of service}} and {{Privacy Policy}}. We may occasionally send you newsletters with special offers. Don’t worry, you can easily unsubscribe at anytime.",
                  {
                    "{{Terms of service}}": `<span class="fontBlue"><a href="https://${domain}/terms-and-conditions/" target="_blank" rel="noreferrer">${t(
                      "Terms of service"
                    )}</a></span>`,
                    "{{Privacy Policy}}": ` <span class="fontBlue"><a href="https://${domain}/privacy-policy/" target="_blank" rel="noreferrer">${t(
                      "Privacy Policy"
                    )}</a></span>`
                  }
                )}
              </p>
            `}
          </Raw>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: stateType) => ({
  orderDetails: state.orderReducer.details,
  cart: state.cartReducer.cart,
  extras: state.cartReducer.extras,
  checkoutError: state.commonReloadReducer.checkoutError,
  emailBlacklisted: state.commonReloadReducer.emailBlacklisted,
  pageCount: state.commonReducer.pageCount
});

const mapDispatchToProps = (dispatch: any) => ({
  setOrderDetails: (details: Partial<orderType>) => {
    dispatch(setOrderDetails(details) as any);
  },
  setEmailBlackListed: (data: boolean) => {
    dispatch(setEmailBlacklisted(data));
  },
  setPageCount: (data: number) => {
    dispatch(setPageCount(data));
  },
  setCheckOutError: (data: boolean) => {
    dispatch(setCheckOutError(data));
  }
});

const ConnectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(CheckOutAddress);

export default ConnectedComponent;

export const DateTimeInfoHeader = ({
  date,
  endTime,
  startTime
}: {
  date: string | undefined;
  startTime: string | undefined;
  endTime: string | undefined;
}) => {
  return date && startTime && endTime ? (
    <div className="checkoutAddress_introText">
      <div>Chosen cleaning time</div>
      <div className="checkoutAddress_introText_bottom">
        <div>
          <b>{moment(date).format("MMM DD, YYYY")}</b> from{" "}
          <b>
            {startTime || "-"} to {endTime || "-"}
          </b>
          .
        </div>
        <span>Free rescheduling up to 24 hours before your booking.</span>
      </div>
    </div>
  ) : null;
};
